import { useMediaQuery, useTheme } from "@mui/material";
import { useContext, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { GlobalContext } from "store/globalContext";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { showError } from "store/reducers/errorReducer";
import { hideSpinner, showSpinner } from "store/reducers/spinnerReducer";

export const steps = {
  SELECT_PRACTICE: 1,
  CREATE_SENDER: 2,
};

export const usePublicSignUpSender = ({
  defaultValues,
  startStep = steps.SELECT_PRACTICE,
}) => {
  const dispatch = useAppDispatch();
  const { userData, senderData, loadSenderData } = useContext(GlobalContext);
  const isLoading = useAppSelector((state) => !!state.spinner);
  const [isSignUpDone, setIsSignUpDone] = useState(false);
  const [step, setStep] = useState(startStep);
  const [currentRecipient, setCurrentRecipient] = useState({
    id: "",
    name: "",
  });
  const [currentPractice, setCurrentPractice] = useState({ id: "", name: "" });
  const [isAddNewDialogOpen, setIsAddNewDialogOpen] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const { control, handleSubmit, watch, setError, setValue } = useForm({
    defaultValues,
  });

  const password = useRef("");
  password.current = watch("password", "");

  const setSelectedRecipient = ({ id, name }) => {
    setCurrentRecipient({ id, name });
    setValue("recipientId", id, { shouldDirty: true });
  };

  const setSelectedPractice = ({ id, name }) => {
    setCurrentPractice({ id, name });
    setValue("practiceId", id, { shouldDirty: true });
  };

  const onSubmit = async (data) => {
    dispatch(showSpinner());

    if (!senderData?.email && userData?.fullName) {
      const [firstName = "", lastName = "", ...rest] =
        userData?.fullName?.split(" ");
      data.firstName = firstName;
      data.lastName = [lastName, ...rest].join(" ");
      data.email = userData?.email;
      data.practiceId = userData.organizationId;
    }

    if (senderData?.email) {
      data.originatorId = senderData?.id;
      data.practiceId = senderData.organizationId;
      data.email = senderData.email;
    }

    data.firstName = data.firstName.trim();
    data.lastName = data.lastName.trim();
    data.jobTitle = data.jobTitle.trim();

    let result;
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/create_sender`,
        {
          method: "PUT",
          body: JSON.stringify(data),
        }
      );
      result = await response.json();
    } catch (error) {
      dispatch(showError(error));
      dispatch(hideSpinner());
      return;
    }

    if (result?.error) {
      if (result?.error === "Invalid code") {
        setError("code", {
          message: "Invalid code",
        });
        dispatch(hideSpinner());
        return;
      }
      dispatch(showError(result?.error));
      dispatch(hideSpinner());
      return;
    }

    dispatch(hideSpinner());
    setIsSignUpDone(true);
    loadSenderData();
  };

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const isAgreeToTerms = watch("isAgreeToTerms");

  const recipientId = watch("recipientId");
  const practiceId = watch("practiceId");

  const isPhoneRequired = !senderData?.phone && !userData?.phone;

  return {
    isLoading,
    step,
    setStep,
    currentRecipient,
    currentPractice,
    isAddNewDialogOpen,
    setIsAddNewDialogOpen,
    setSelectedRecipient,
    setSelectedPractice,
    control,
    handleSubmit,
    onSubmit,
    isAgreeToTerms,
    recipientId,
    practiceId,
    theme,
    fullScreen,
    isSignUpDone,
    setIsSignUpDone,
    setValue,
    steps,
    isPhoneRequired,
    isPasswordVisible,
    setIsPasswordVisible,
    password,
  };
};
